import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { LanguageToolPage } from '@/components/astro/new-design-25/SEO/language-tool-page';
import { hreflangLinks, seo } from '@/seo/seo';

const faqTitle = 'French text to speech FAQ';
const faqDescription =
  'Quick answers about converting French text to speech with Listening.';
const faqs = [
  {
    question: 'Which French voices are available?',
    answer:
      'Listening offers natural metropolitan French (the voice you hear on Radio France) and Canadian French (Québécois) voices in male and female options. Choose the accent that matches your audience.',
  },
  {
    question: 'How are liaisons and elisions handled?',
    answer:
      'Our French voices produce natural enchaînements, liaisons, and dropped vowels rather than reading word by word. The audio flows the way native French actually sounds.',
  },
  {
    question: 'Is it accurate on French academic vocabulary?',
    answer:
      'Yes. The voices are trained on French academic and literary content, so philosophical, literary, and technical terminology pronounces correctly.',
  },
  {
    question: 'Can I use this for French language learning?',
    answer:
      'Yes. Slow the playback to 0.5x to catch every syllable, then speed up as your ear improves. Synced highlighting in the Listening app lets you read along while listening.',
  },
];

const heroTitle = 'French Text to Speech';
const heroDescription =
  'French text to speech with natural Parisian and Canadian French voices. Built for academic papers, language learning, and professional content with proper liaisons, elisions, and accentuation.';

const featureTitle = 'the French TTS built for fluent listeners and learners:';
const featureSubtitle = '';
const featureDescription = '';
const featureBadge = 'French voices';
const featureVariant = 'default';
const featureCtaText = 'Try now';

const featureFeatures = [
  {
    title: 'Parisian and Canadian Variants',
    description:
      'Switch between metropolitan French and Québécois voices so the audio matches the audience you are writing for.',
  },
  {
    title: 'Liaisons and Elisions Handled',
    description:
      'Natural enchaînements, liaisons, and dropped vowels, not the broken word-by-word delivery of generic French TTS.',
  },
  {
    title: 'Academic Vocabulary',
    description:
      'Trained on serious French prose: research papers, literary criticism, philosophy, and technical writing.',
  },
  {
    title: 'Right for Language Learners',
    description:
      'Slow down to 0.5x to catch every syllable, then speed back up as your ear improves on real French content.',
  },
];

const solutionBenefitsData = {
  title: 'Why francophone listeners stay with our French TTS',
  items: [
    {
      text: 'Natural Parisian and Québécois voices, picked per project so the register fits the audience',
      highlight: 'Parisian and Québécois voices',
    },
    {
      text: 'Proper handling of accents, cedillas, and liaisons that generic French voices mangle',
      highlight: 'accents, cedillas, and liaisons',
    },
    {
      text: 'Clean pronunciation of French academic terminology, philosophical vocabulary, and proper nouns',
      highlight: 'academic terminology',
    },
    {
      text: 'Adjustable speed from 0.5x to 4x, ideal for shadowing exercises and language-learning workflows',
      highlight: 'shadowing exercises',
    },
  ],
};

const useCasesData = {
  title: 'How people use French text to speech',
  description:
    'From students at the Sorbonne to French learners halfway across the world, here is how our French voices get used.',
  useCases: [
    {
      title: 'University Student in France',
      challenge:
        'Work through dense French theoretical texts in literature, philosophy, sociology, or political science.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Listen to assigned readings in natural French while commuting, with adjustable speed for hard passages.',
        },
        {
          label: 'Generic TTS',
          content:
            'Robotic delivery turns Foucault, Bourdieu, or Levinas into nonsense, and you stop using it after one chapter.',
        },
      ],
    },
    {
      title: 'French Learner Building Real Listening Skill',
      challenge:
        'Get past textbook audio and into real French content at a pace you can actually follow.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Paste any French article, blog, or chapter, slow it to 0.7x, and shadow it with native pronunciation.',
        },
        {
          label: 'Generic TTS',
          content:
            'Wooden phrasing teaches you the wrong rhythm and makes real native French even harder to understand later.',
        },
      ],
    },
    {
      title: 'Québécois Researcher or Educator',
      challenge:
        'Produce French audio content for Québec and Canadian francophone audiences, not just metropolitan French.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Pick a Canadian French voice so course materials and research summaries sound right to a Québécois ear.',
        },
        {
          label: 'Generic TTS',
          content:
            'One generic European-only voice that signals immediately the content was not made for a Canadian audience.',
        },
      ],
    },
    {
      title: 'Professional Producing French Content',
      challenge:
        'Turn newsletters, internal memos, or reports into French audio that is comfortable to listen to.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Generate natural French read-alongs on demand, with the same voices across web, mobile, and the Chrome extension.',
        },
        {
          label: 'Generic TTS',
          content:
            'Audio that feels unprofessional and clearly machine-made undermines the credibility of the content itself.',
        },
      ],
    },
  ],
};

const seoSections = [
  {
    title: 'What French Text to Speech Has to Get Right',
    content:
      'French is harder for TTS than English. Liaisons connect words across boundaries, vowels elide together, silent consonants come alive depending on what follows, and accents change pronunciation in ways that change meaning. A generic French voice that reads word by word, leaving liaisons out and dropping the music of the language, makes everything sound foreign and amateurish, even to a non-native ear. To real French speakers and serious learners, it is unusable.',
    contentSecondary:
      'Listening was built for full paragraphs of real French, not for marketing taglines. Our voices have been trained on academic, literary, and journalistic French content. We support metropolitan French (the voice you hear on Radio France) and Canadian French (the voice you hear in Québec media). The result is audio you can actually leave running for an hour without feeling the urge to turn it off.',
  },
  {
    title: 'French Text to Speech for Study, Work, and Language Learning',
    content:
      'French learners get more out of native input than from grammar drills, but native input is hard to find in formats you can control. Slowing real French audio down usually distorts it. Speeding it up usually mangles it. Our French TTS lets you take any French text you find — an article, a chapter, a poem — and turn it into clean audio at any speed, with synced highlighting in the app so you can read and listen at the same time.',
    contentSecondary:
      'For French-speaking students and researchers, it solves a different problem: keeping up with the volume of reading expected in French academic life. Paste papers, chapters, or notes into the converter on this page, generate audio, and open a 24-hour read-along link with classmates. The same voices, accents, and behavior are available in the Listening app and the Chrome extension when you are ready for the full workflow.',
  },
];

export const Route = createFileRoute('/_marketing/_seo/french-text-to-speech')({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'French Text to Speech — Natural Parisian & Canadian French Voices | Listening',
        description:
          'French text to speech with natural Parisian and Canadian French AI voices. Built for academic content, language learning, and professional French audio. Free demo.',
      }),
    ],
    links: [...hreflangLinks('/french-text-to-speech')],
  }),
  component: () => (
    <LanguageToolPage
      converterHeading={heroTitle}
      converterSubtitle={heroDescription}
      faqDescription={faqDescription}
      faqTitle={faqTitle}
      faqs={faqs}
      featureBadge={featureBadge}
      featureCtaText={featureCtaText}
      featureDescription={featureDescription}
      featureFeatures={featureFeatures}
      featureSubtitle={featureSubtitle}
      featureTitle={featureTitle}
      featureVariant={featureVariant}
      preferredLang='fr'
      seoSections={seoSections}
      solutionBenefitsData={solutionBenefitsData}
      useCasesData={useCasesData}
    />
  ),
});
